import { mapApiRowToFeatureDocument } from '@/app/dashboard/feature-document/services/featureDocumentApi';
import type { FeatureDocument } from '@/app/dashboard/feature-document/types';
import {
  findKeyFeaturePageByMenuUrl,
  resolveCanonicalFeatureMenuUrl,
} from '@/lib/keyFeaturePages';
import {
  assertApiBodyOk,
  isApiBodyStatusError,
} from '@/utils/apiBusinessError';
import publicBrochureApi from './publicBrochureApi';
import {
  RESOURCE_DOCUMENT_TYPES,
  type ResourceDocumentType,
} from '../utils/resourceDocumentConstants';

/** Resource document record for public marketing download flows. */
export type PublicResourceDocument = PublicBrochureDocument;

export type PublicBrochureDocument = FeatureDocument & {
  s3_bucket_path?: string;
  s3_bucket_url?: string;
  /** Resolved download URL from lookup response fields. */
  documentUrl?: string;
};

export type HeroResourceDocuments = {
  whitePaper: PublicBrochureDocument | null;
  executiveSummary: PublicBrochureDocument | null;
};

/** Primary-first brochure download URL fields from API records. */
const BROCHURE_DOWNLOAD_URL_FIELD_KEYS = [
  's3_bucket_url',
  'p_s3_bucket_url',
  's3_bucket_path',
  'p_s3_bucket_path',
  'download_url',
  'p_download_url',
  'signed_url',
  'p_signed_url',
  'presigned_url',
  'p_presigned_url',
  'document_url',
  'p_document_url',
  'file_url',
  'p_file_url',
  's3_document_location',
  'p_s3_document_location',
] as const;

/** Fresh download URL fields from POST /v1/feature_document_download/new response. */
const POST_DOWNLOAD_URL_FIELD_KEYS = [
  's3_bucket_url',
  'p_s3_bucket_url',
  'download_url',
  'p_download_url',
  'signed_url',
  'p_signed_url',
  'presigned_url',
  'p_presigned_url',
  'document_url',
  'p_document_url',
  'file_url',
  'p_file_url',
] as const;

const POST_DOWNLOAD_ID_FIELD_KEYS = [
  'feature_document_download_id',
  'p_feature_document_download_id',
  'id',
  'p_id',
] as const;

const BROCHURE_UNAVAILABLE_MESSAGE =
  'Brochure download is not available right now. Please try again later or contact support.';

export type BrochureDownloadUrlSource = 'post' | 'fresh_lookup' | 'cached';

export type BrochureDownloadFlowResult = {
  downloadUrl: string;
  urlSource: BrochureDownloadUrlSource;
  featureDocumentDownloadId: number | null;
};

/** POST /v1/feature_document_download/send_pdf_email — isolated swap point. */
export async function sendFeatureDocumentDownloadPdfEmail(
  featureDocumentDownloadId: number,
): Promise<void> {
  await publicBrochureApi
    .post('/v1/feature_document_download/send_pdf_email', {
      p_feature_document_download_id: String(featureDocumentDownloadId),
    })
    .then((response) => {
      if (isApiBodyStatusError(response.data)) {
        throw { status: 400, details: response.data };
      }
      return response;
    })
    .catch(() => {
      throw new Error('EMAIL_SEND_FAILED');
    });
}

/** @deprecated Use sendFeatureDocumentDownloadPdfEmail */
export const sendResourceDocumentEmail = sendFeatureDocumentDownloadPdfEmail;

/** @deprecated Use sendFeatureDocumentDownloadPdfEmail */
export const sendBrochureEmail = sendFeatureDocumentDownloadPdfEmail;

type BrochureDownloadTimings = {
  leadSubmitMs: number;
  urlResolveMs: number;
  openTriggerMs: number;
  totalMs: number;
  urlSource: BrochureDownloadUrlSource;
  urlType: string;
};

let cachedIpAddressPromise: Promise<string> | null = null;

/** Warm IP lookup while the user fills the brochure form (dev timing shows submit delay). */
export function prefetchBrochureSubmitContext(): void {
  if (typeof window === 'undefined') return;
  if (!cachedIpAddressPromise) {
    cachedIpAddressPromise = getUserIpAddress();
  }
}

function isDevEnvironment(): boolean {
  return process.env.NODE_ENV === 'development';
}

function classifyDownloadUrl(url: string): string {
  if (url.startsWith('blob:')) return 'blob/object URL';
  if (url.startsWith('data:')) return 'base64/data URL';
  if (url.includes('/files/download?url=')) return '/files/download?url=...';
  if (url.includes('X-Amz-Signature')) return 'direct S3 presigned URL';
  if (/amazonaws\.com/i.test(url)) return 'direct S3 URL';
  return 'backend/other HTTP URL';
}

function logBrochureDownloadFlowTimings(timings: BrochureDownloadTimings, url: string): void {
  if (!isDevEnvironment()) return;

  console.groupCollapsed('[brochure-download] flow timings');
  console.log('lead submit API (ms):', timings.leadSubmitMs.toFixed(1));
  console.log('URL resolve / fresh lookup (ms):', timings.urlResolveMs.toFixed(1));
  console.log('open trigger (ms):', timings.openTriggerMs.toFixed(1));
  console.log('total flow (ms):', timings.totalMs.toFixed(1));
  console.log('URL source:', timings.urlSource);
  console.log('URL type:', timings.urlType);
  console.log('URL length:', url.length);
  console.log('has X-Amz-Signature:', url.includes('X-Amz-Signature'));
  console.log('has X-Amz-Algorithm:', url.includes('X-Amz-Algorithm'));
  console.log('has X-Amz-Credential:', url.includes('X-Amz-Credential'));
  console.groupEnd();
}

export interface BrochureDownloadFormValues {
  firstName: string;
  lastName: string;
  email: string;
  companyName: string;
  title: string;
  phoneNumber: string;
}

/** Swagger POST /v1/feature_document_download/new */
export type FeatureDocumentDownloadCreateBody = {
  p_first_name: string;
  p_last_name: string;
  p_email: string;
  p_company_name: string;
  p_title: string;
  p_phone_number: string;
  p_ip_address: string;
  p_user_device: string;
  p_user_referral_location: string;
  p_feature_document_id: string;
  p_feature: string;
  p_feature_menu_url: string;
  p_document_name: string;
  p_s3_document_location: string;
};

const OSHA_CANONICAL_PATH = '/features/osha-respiratory-protection';
const OSHA_LEGACY_PATH = '/osha-respiratory-protection';

/** Default document type for legacy brochure-only lookups. */
const LEGACY_BROCHURE_LOOKUP_DOCUMENT_TYPE = RESOURCE_DOCUMENT_TYPES.BROCHURE;

function asArray<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object') return [data as T];
  return [];
}

function trimUrlOnly(url: string): string {
  return url.trim();
}

function isHttpUrl(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

function hasAwsPresignedQueryParams(url: string): boolean {
  return (
    url.includes('X-Amz-Signature') ||
    url.includes('X-Amz-Algorithm') ||
    url.includes('X-Amz-Credential')
  );
}

/** AWS signed URLs must be opened exactly as returned — trim whitespace only. */
function isFullPresignedDownloadUrl(url: string): boolean {
  const trimmed = trimUrlOnly(url);
  return isHttpUrl(trimmed) && hasAwsPresignedQueryParams(trimmed);
}

function isRejectedDownloadUrl(url: string): boolean {
  return url.includes('/files/download?url=');
}

function extractPostResponseSources(data: unknown): Record<string, unknown>[] {
  if (!data) return [];

  if (isApiBodyStatusError(data)) return [];

  const rows = asArray<Record<string, unknown>>(data);
  if (rows.length > 0) return rows;

  if (typeof data === 'object' && data !== null) {
    return [data as Record<string, unknown>];
  }

  return [];
}

/** Parse feature_document_download_id from POST /v1/feature_document_download/new response. */
export function resolveFeatureDocumentDownloadId(data: unknown): number | null {
  const sources = extractPostResponseSources(data);

  for (const key of POST_DOWNLOAD_ID_FIELD_KEYS) {
    for (const source of sources) {
      const raw = source[key];
      if (raw === null || raw === undefined || raw === '') continue;
      const id = Number(raw);
      if (Number.isFinite(id) && id > 0) return id;
    }
  }

  return null;
}

function resolveFreshUrlFromPostResponse(data: unknown): string | null {
  if (!data) return null;

  if (isApiBodyStatusError(data)) return null;

  const sources = extractPostResponseSources(data);

  for (const key of POST_DOWNLOAD_URL_FIELD_KEYS) {
    for (const source of sources) {
      const url = trimUrlOnly(String(source[key] ?? ''));
      if (!url || !isHttpUrl(url) || isRejectedDownloadUrl(url)) continue;
      return url;
    }
  }

  return null;
}

async function resolveBrochureDownloadUrlAfterSubmit(
  postResponseData: unknown,
  cachedDocument: PublicBrochureDocument,
  featureMenuPathname: string,
): Promise<BrochureDownloadFlowResult | null> {
  const postUrl = resolveFreshUrlFromPostResponse(postResponseData);
  if (postUrl) {
    return {
      downloadUrl: postUrl,
      urlSource: 'post',
      featureDocumentDownloadId: resolveFeatureDocumentDownloadId(postResponseData),
    };
  }

  const documentType =
    cachedDocument.document_type?.trim() || LEGACY_BROCHURE_LOOKUP_DOCUMENT_TYPE;

  const freshDocument = await getFeatureDocumentByFeatureMenuUrl(
    featureMenuPathname,
    documentType as ResourceDocumentType,
  );
  if (freshDocument) {
    const freshUrl = getBrochureDownloadUrl(freshDocument);
    if (freshUrl) {
      return {
        downloadUrl: freshUrl,
        urlSource: 'fresh_lookup',
        featureDocumentDownloadId: resolveFeatureDocumentDownloadId(postResponseData),
      };
    }
  }

  const cachedUrl = getBrochureDownloadUrl(cachedDocument);
  if (cachedUrl) {
    return {
      downloadUrl: cachedUrl,
      urlSource: 'cached',
      featureDocumentDownloadId: resolveFeatureDocumentDownloadId(postResponseData),
    };
  }

  return null;
}

function unwrapFilesDownloadProxy(url: string): string {
  const trimmed = trimUrlOnly(url);
  if (!trimmed) return trimmed;

  const wrappedMatch = trimmed.match(/\/files\/download\?url=([^&]+)/i);
  if (!wrappedMatch) {
    return trimmed;
  }

  // Proxy wrapper only — do not apply to direct s3_bucket_url values.
  return wrappedMatch[1];
}

function pickFallbackBrochureUrl(
  sources: Array<Record<string, unknown> | PublicBrochureDocument | undefined>,
  skipDocumentLocation: boolean,
): string | null {
  for (const key of BROCHURE_DOWNLOAD_URL_FIELD_KEYS) {
    if (
      skipDocumentLocation &&
      (key === 's3_document_location' || key === 'p_s3_document_location')
    ) {
      continue;
    }

    if (key === 's3_bucket_url' || key === 'p_s3_bucket_url') {
      continue;
    }

    for (const source of sources) {
      if (!source) continue;
      const url = String((source as Record<string, unknown>)[key] ?? '').trim();
      if (!url) continue;

      const finalUrl = unwrapFilesDownloadProxy(url);
      if (isRejectedDownloadUrl(finalUrl)) {
        continue;
      }
      return finalUrl;
    }
  }

  return null;
}

function resolveS3BucketDownloadUrl(
  sources: Array<Record<string, unknown> | PublicBrochureDocument | undefined>,
): string | null {
  for (const source of sources) {
    if (!source) continue;
    const bucketUrl = String(
      (source as Record<string, unknown>).s3_bucket_url ??
        (source as Record<string, unknown>).p_s3_bucket_url ??
        (source as PublicBrochureDocument).s3_bucket_url ??
        '',
    ).trim();

    if (!bucketUrl) continue;
    if (!isFullPresignedDownloadUrl(bucketUrl)) continue;

    return bucketUrl;
  }

  return null;
}

function resolveBrochureDocumentUrlFromRecord(
  raw: Record<string, unknown>,
  document?: PublicBrochureDocument,
): string | null {
  const sources = [raw, document];
  const bucketUrl = resolveS3BucketDownloadUrl(sources);
  if (bucketUrl) {
    return bucketUrl;
  }

  const hasBucketUrlField = sources.some((source) => {
    if (!source) return false;
    const value = String(
      (source as Record<string, unknown>).s3_bucket_url ??
        (source as Record<string, unknown>).p_s3_bucket_url ??
        (source as PublicBrochureDocument).s3_bucket_url ??
        '',
    ).trim();
    return Boolean(value);
  });

  return pickFallbackBrochureUrl(sources, hasBucketUrlField);
}

function mapPublicBrochureDocument(raw: Record<string, unknown>): PublicBrochureDocument {
  const document = mapApiRowToFeatureDocument(raw);
  const bucketPath = String(raw.s3_bucket_path ?? raw.p_s3_bucket_path ?? '').trim();
  const bucketUrl = String(raw.s3_bucket_url ?? raw.p_s3_bucket_url ?? '').trim();
  const documentLocation = String(
    raw.s3_document_location ??
      raw.p_s3_document_location ??
      document.s3_document_location ??
      '',
  ).trim();
  const mapped: PublicBrochureDocument = {
    ...document,
    s3_bucket_path: bucketPath || undefined,
    s3_bucket_url: bucketUrl || undefined,
    s3_document_location: documentLocation || document.s3_document_location,
  };
  const documentUrl = resolveBrochureDocumentUrlFromRecord(raw, mapped) ?? undefined;

  return {
    ...mapped,
    documentUrl,
  };
}

function resolveBrochureDocumentUrl(document: PublicBrochureDocument): string | null {
  const bucketUrl = resolveS3BucketDownloadUrl([document]);
  if (bucketUrl) {
    return bucketUrl;
  }

  const hasBucketUrl = Boolean(document.s3_bucket_url?.trim());
  if (hasBucketUrl) {
    return null;
  }

  const cachedUrl = document.documentUrl?.trim();
  if (cachedUrl && isFullPresignedDownloadUrl(cachedUrl)) {
    return cachedUrl;
  }

  return pickFallbackBrochureUrl([document], false);
}

function isActiveBrochureDocument(
  document: PublicBrochureDocument | null,
): document is PublicBrochureDocument {
  if (!document) return false;
  if (document.active_flag === false) return false;
  if (!document.feature_document_id) return false;
  return Boolean(resolveBrochureDocumentUrl(document));
}

function parseBrochureLookupResponse(data: unknown): PublicBrochureDocument | null {
  if (!data) return null;

  if (isApiBodyStatusError(data)) return null;

  const rows = asArray<Record<string, unknown>>(data);
  for (const row of rows) {
    const document = mapPublicBrochureDocument(row);
    if (isActiveBrochureDocument(document)) {
      return document;
    }
  }

  return null;
}

async function fetchResourceDocumentByFeatureAndMenuUrl(
  featureMenuUrl: string,
  feature: string,
  documentType: ResourceDocumentType,
): Promise<PublicBrochureDocument | null> {
  try {
    const response = await publicBrochureApi.get(
      '/v1/feature_document/by_feature_and_feature_menu_url',
      {
        params: {
          p_feature: feature,
          p_feature_menu_url: featureMenuUrl,
          p_document_type: documentType,
        },
      },
    );

    if (isApiBodyStatusError(response.data)) {
      return null;
    }

    return parseBrochureLookupResponse(response.data);
  } catch {
    return null;
  }
}

/** Resolve a feature document for the current public feature page pathname. */
export async function getFeatureDocumentByFeatureMenuUrl(
  featureMenuUrl: string,
  documentType: ResourceDocumentType = LEGACY_BROCHURE_LOOKUP_DOCUMENT_TYPE,
): Promise<PublicBrochureDocument | null> {
  const rawPath = (featureMenuUrl ?? '').trim();
  if (!rawPath) return null;

  const canonicalPath = resolveCanonicalFeatureMenuUrl(rawPath) || rawPath;
  const lookupPaths =
    canonicalPath === OSHA_CANONICAL_PATH
      ? [canonicalPath, OSHA_LEGACY_PATH]
      : [canonicalPath];

  for (const path of lookupPaths) {
    const keyFeaturePage = findKeyFeaturePageByMenuUrl(path);
    const feature = keyFeaturePage?.feature?.trim();
    if (!feature) continue;

    const document = await fetchResourceDocumentByFeatureAndMenuUrl(
      path,
      feature,
      documentType,
    );
    if (document) return document;
  }

  return null;
}

/** Hero resource buttons: White Paper and Executive Summary for the current feature page. */
export async function getHeroResourceDocumentsForPage(
  featureMenuUrl: string,
): Promise<HeroResourceDocuments> {
  const [whitePaper, executiveSummary] = await Promise.all([
    getFeatureDocumentByFeatureMenuUrl(
      featureMenuUrl,
      RESOURCE_DOCUMENT_TYPES.WHITE_PAPER,
    ),
    getFeatureDocumentByFeatureMenuUrl(
      featureMenuUrl,
      RESOURCE_DOCUMENT_TYPES.EXECUTIVE_SUMMARY,
    ),
  ]);

  return { whitePaper, executiveSummary };
}

function isDashboardReferrer(referrer: string): boolean {
  if (typeof window === 'undefined') return false;

  try {
    const refUrl = new URL(referrer);
    if (refUrl.origin !== window.location.origin) return false;
    return refUrl.pathname.toLowerCase().startsWith('/dashboard');
  } catch {
    return false;
  }
}

function buildCurrentPageReferralLocation(featureMenuPathname?: string): string {
  if (typeof window === 'undefined') {
    const path = featureMenuPathname?.trim();
    return path || '-';
  }

  const path = (featureMenuPathname?.trim() || window.location.pathname).trim();
  if (!path) return '-';

  return `${window.location.origin}${path.startsWith('/') ? path : `/${path}`}`;
}

export function buildUserReferralLocation(featureMenuPathname?: string): string {
  if (typeof document !== 'undefined') {
    const referrer = document.referrer?.trim();
    if (referrer && !isDashboardReferrer(referrer)) {
      return referrer;
    }
  }

  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    const campaignKeys = [
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_term',
      'utm_content',
      'ref',
      'source',
    ];
    const campaignParts = campaignKeys
      .map((key) => {
        const value = params.get(key);
        return value ? `${key}=${value}` : null;
      })
      .filter(Boolean);

    if (campaignParts.length > 0) {
      return campaignParts.join('&');
    }
  }

  return buildCurrentPageReferralLocation(featureMenuPathname);
}

async function getUserIpAddress(): Promise<string> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 800);

    const ipResponse = await fetch('https://api.ipify.org?format=json', {
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    if (ipResponse.ok) {
      const ipData = (await ipResponse.json()) as { ip?: string };
      return ipData.ip?.trim() || '';
    }
  } catch {
    // Continue without IP if lookup fails or times out.
  }

  return '';
}

async function resolveSubmitIpAddress(): Promise<string> {
  if (!cachedIpAddressPromise) {
    cachedIpAddressPromise = getUserIpAddress();
  }
  return cachedIpAddressPromise;
}

function resolveS3DocumentLocationForPayload(document: PublicBrochureDocument): string {
  return (
    document.s3_document_location?.trim() ||
    document.s3_bucket_path?.trim() ||
    ''
  );
}

/** Resolved brochure URL stored on the document record. */
export function getBrochureDownloadUrl(document: PublicBrochureDocument): string | null {
  return resolveBrochureDocumentUrl(document);
}

export function buildFeatureDocumentDownloadPayload(
  form: BrochureDownloadFormValues,
  document: PublicBrochureDocument,
  featureMenuPathname: string,
  ipAddress: string,
): FeatureDocumentDownloadCreateBody {
  const canonicalMenuUrl =
    resolveCanonicalFeatureMenuUrl(featureMenuPathname) || featureMenuPathname.trim();

  return {
    p_first_name: form.firstName.trim(),
    p_last_name: form.lastName.trim(),
    p_email: form.email.trim(),
    p_company_name: form.companyName.trim() || '',
    p_title: form.title.trim() || '',
    p_phone_number: form.phoneNumber.trim() || '',
    p_ip_address: ipAddress,
    p_user_device: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    p_user_referral_location: buildUserReferralLocation(canonicalMenuUrl || featureMenuPathname.trim()),
    p_feature_document_id: String(document.feature_document_id),
    p_feature: document.feature.trim(),
    p_feature_menu_url: canonicalMenuUrl || document.feature_menu_url.trim(),
    p_document_name: document.document_name.trim(),
    p_s3_document_location: resolveS3DocumentLocationForPayload(document),
  };
}

export async function submitFeatureDocumentDownload(
  form: BrochureDownloadFormValues,
  document: PublicBrochureDocument,
  featureMenuPathname: string,
): Promise<unknown> {
  const ipAddress = await resolveSubmitIpAddress();
  const body = buildFeatureDocumentDownloadPayload(
    form,
    document,
    featureMenuPathname,
    ipAddress,
  );

  const response = await publicBrochureApi.post('/v1/feature_document_download/new', body);

  assertApiBodyOk(
    response.data,
    'Failed to save your brochure download request.',
  );

  return response.data;
}

function logBrochureDownloadUrlDiagnostics(url: string, urlSource: BrochureDownloadUrlSource): void {
  if (!isDevEnvironment()) return;

  console.log('[brochure-download] opening URL', {
    urlSource,
    urlType: classifyDownloadUrl(url),
    length: url.length,
    hasXAmzSignature: url.includes('X-Amz-Signature'),
    hasXAmzAlgorithm: url.includes('X-Amz-Algorithm'),
    hasXAmzCredential: url.includes('X-Amz-Credential'),
  });
}

export function getBrochureFileName(document: PublicBrochureDocument): string {
  const raw = document.document_name?.trim() || 'CareAscendium-Brochure';
  const sanitized = raw.replace(/[<>:"/\\|?*\x00-\x1F]/g, '').trim();
  const base = sanitized || 'CareAscendium-Brochure';
  return /\.pdf$/i.test(base) ? base : `${base}.pdf`;
}

/** Save lead + resolve download URL — does not open or download. */
export async function submitBrochureLeadAndResolveUrl(
  form: BrochureDownloadFormValues,
  document: PublicBrochureDocument,
  featureMenuPathname: string,
): Promise<BrochureDownloadFlowResult> {
  const flowStart = performance.now();

  const leadSubmitStart = performance.now();
  const postResponseData = await submitFeatureDocumentDownload(
    form,
    document,
    featureMenuPathname,
  );
  const leadSubmitMs = performance.now() - leadSubmitStart;

  const urlResolveStart = performance.now();
  const resolved = await resolveBrochureDownloadUrlAfterSubmit(
    postResponseData,
    document,
    featureMenuPathname,
  );
  const urlResolveMs = performance.now() - urlResolveStart;

  if (!resolved) {
    throw new Error(BROCHURE_UNAVAILABLE_MESSAGE);
  }

  logBrochureDownloadFlowTimings(
    {
      leadSubmitMs,
      urlResolveMs,
      openTriggerMs: 0,
      totalMs: performance.now() - flowStart,
      urlSource: resolved.urlSource,
      urlType: classifyDownloadUrl(resolved.downloadUrl),
    },
    resolved.downloadUrl,
  );

  return resolved;
}

/** Same-page file download via anchor — no new tab. */
export function triggerBrochureFileDownload(url: string, fileName?: string): void {
  const trimmed = trimUrlOnly(url);
  if (!trimmed) {
    throw new Error(BROCHURE_UNAVAILABLE_MESSAGE);
  }

  if (isRejectedDownloadUrl(trimmed)) {
    throw new Error(BROCHURE_UNAVAILABLE_MESSAGE);
  }

  const link = document.createElement('a');
  link.href = trimmed;
  link.download = fileName?.trim() || 'CareAscendium-Brochure.pdf';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function openBrochureDownloadUrl(
  documentUrl: string,
  urlSource: BrochureDownloadUrlSource = 'cached',
): void {
  const url = trimUrlOnly(documentUrl);
  if (!url) {
    throw new Error(BROCHURE_UNAVAILABLE_MESSAGE);
  }

  if (isRejectedDownloadUrl(url)) {
    throw new Error(BROCHURE_UNAVAILABLE_MESSAGE);
  }

  logBrochureDownloadUrlDiagnostics(url, urlSource);

  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function openBrochureInNewTab(document: PublicBrochureDocument): void {
  const documentUrl = getBrochureDownloadUrl(document);
  if (!documentUrl) {
    throw new Error(BROCHURE_UNAVAILABLE_MESSAGE);
  }

  openBrochureDownloadUrl(documentUrl, 'cached');
}

/** @deprecated Prefer submitBrochureLeadAndResolveUrl + triggerBrochureFileDownload. */
export async function completeBrochureDownloadFlow(
  form: BrochureDownloadFormValues,
  document: PublicBrochureDocument,
  featureMenuPathname: string,
): Promise<BrochureDownloadFlowResult> {
  const resolved = await submitBrochureLeadAndResolveUrl(form, document, featureMenuPathname);
  openBrochureDownloadUrl(resolved.downloadUrl, resolved.urlSource);
  return resolved;
}
